import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectControllerButton } from '@/components/arcade/connect-controller-button';
import { useProControllers } from '@/hooks/use-pro-controllers';
import {
  type ArcadeControlCode,
  formatArcadeInputLabel,
  getArcadeControlCodesForInput,
} from '@/lib/arcade-controls';
import {
  type ProControllerButtons,
  type ProControllerState,
  proControllerToArcadeControls,
} from '@/lib/pro-controller';

interface LastKey {
  key: string;
  code: string;
  keyCode: number;
  token: string;
}

interface GamepadSnapshot {
  index: number;
  id: string;
  buttons: { pressed: boolean; value: number }[];
  axes: number[];
}

// The value an admin should type into the event physical key map field. Mirrors
// how mappings are stored/normalized (lowercased, space as the "space" token).
function keyToToken(key: string): string {
  if (key === ' ' || key === 'Spacebar' || key === 'Space') {
    return 'space';
  }
  return key.toLowerCase();
}

function mappedControlCodes(token: string): string[] {
  // Pass null mapping to resolve against DEFAULT_ARCADE_MAPPING.
  return getArcadeControlCodesForInput(null, token);
}

export function ArcadeDebug() {
  const [lastKey, setLastKey] = useState<LastKey | null>(null);
  const [heldTokens, setHeldTokens] = useState<string[]>([]);
  const [gamepads, setGamepads] = useState<GamepadSnapshot[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const heldRef = useRef<Set<string>>(new Set());
  const {
    supported: hidSupported,
    connect: hidConnect,
    snapshotsRef: hidSnapshotsRef,
  } = useProControllers();
  const [proSnapshots, setProSnapshots] = useState<ProControllerState[]>([]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent, pressed: boolean) => {
      // Don't trap browser shortcuts (refresh, devtools, etc.).
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      e.preventDefault();

      const token = keyToToken(e.key);

      if (pressed) {
        if (!e.repeat) {
          setLastKey({
            key: e.key,
            code: e.code,
            keyCode: e.keyCode,
            token,
          });
        }
        heldRef.current.add(token);
      } else {
        heldRef.current.delete(token);
      }

      setHeldTokens(Array.from(heldRef.current));
    };

    const onKeyDown = (e: KeyboardEvent) => handleKey(e, true);
    const onKeyUp = (e: KeyboardEvent) => handleKey(e, false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    let frame: number;

    const poll = () => {
      const pads = navigator.getGamepads?.() ?? [];
      const snapshots: GamepadSnapshot[] = [];

      for (const pad of pads) {
        if (!pad) {
          continue;
        }
        snapshots.push({
          index: pad.index,
          id: pad.id,
          buttons: pad.buttons.map((b) => ({
            pressed: b.pressed,
            value: b.value,
          })),
          axes: Array.from(pad.axes),
        });
      }

      setGamepads(snapshots);
      frame = requestAnimationFrame(poll);
    };

    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    let frame: number;
    const tick = () => {
      setProSnapshots([...hidSnapshotsRef.current]);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [hidSnapshotsRef]);

  const copyToken = useCallback(async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(token);
      window.setTimeout(() => setCopied(null), 1200);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore.
    }
  }, []);

  const lastKeyControls = lastKey ? mappedControlCodes(lastKey.token) : [];

  return (
    <div className="min-h-screen bg-black p-6 text-primary">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="space-y-1">
          <h1 className="font-bold font-title text-2xl">
            Arcade Controls Debug
          </h1>
          <p className="font-mono text-primary/60 text-sm">
            Press a button on the cabinet. The{' '}
            <span className="text-primary">key token</span> shown below is the
            value to type into an event&apos;s cabinet key mapping.
          </p>
        </header>

        {/* Keyboard */}
        <section className="space-y-4 rounded-lg border-2 border-primary/30 p-5">
          <h2 className="font-semibold font-title text-lg">Keyboard input</h2>

          {lastKey ? (
            <div className="flex flex-wrap items-stretch gap-4">
              <button
                type="button"
                onClick={() => copyToken(lastKey.token)}
                className="group relative flex min-w-44 flex-col items-center justify-center gap-1 rounded-md border-2 border-primary bg-primary/10 px-6 py-4 transition-colors hover:bg-primary/20"
                title="Click to copy"
              >
                <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
                  Key token
                </span>
                <span className="font-bold font-mono text-3xl">
                  {formatArcadeInputLabel(lastKey.token)}
                </span>
                <span className="font-mono text-primary/60 text-xs">
                  type: <span className="text-primary">{lastKey.token}</span>
                </span>
                <span className="absolute top-1 right-2 font-mono text-[10px] text-primary/40 opacity-0 transition-opacity group-hover:opacity-100">
                  {copied === lastKey.token ? 'copied!' : 'copy'}
                </span>
              </button>

              <div className="grid flex-1 grid-cols-3 gap-px overflow-hidden rounded-md border border-primary/20 bg-primary/20 font-mono text-sm">
                <Cell label="event.key" value={JSON.stringify(lastKey.key)} />
                <Cell label="event.code" value={lastKey.code || '—'} />
                <Cell label="keyCode" value={String(lastKey.keyCode)} />
                <Cell
                  label="default control(s)"
                  value={
                    lastKeyControls.length > 0
                      ? lastKeyControls.join(', ')
                      : 'unmapped'
                  }
                  span
                />
              </div>
            </div>
          ) : (
            <p className="font-mono text-primary/40 text-sm">
              Waiting for a keypress…
            </p>
          )}

          <div className="space-y-1.5">
            <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
              Currently held
            </span>
            <div className="flex min-h-8 flex-wrap gap-2">
              {heldTokens.length > 0 ? (
                heldTokens.map((token) => (
                  <span
                    key={token}
                    className="rounded border border-primary bg-primary/15 px-2 py-1 font-bold font-mono text-sm"
                  >
                    {formatArcadeInputLabel(token)}
                  </span>
                ))
              ) : (
                <span className="font-mono text-primary/30 text-sm">none</span>
              )}
            </div>
          </div>
        </section>

        {/* Gamepads */}
        <section className="space-y-4 rounded-lg border-2 border-primary/30 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold font-title text-lg">USB gamepads</h2>
            <span className="font-mono text-primary/60 text-sm">
              {gamepads.length} connected
            </span>
          </div>

          {gamepads.length === 0 ? (
            <p className="font-mono text-primary/40 text-sm">
              No gamepads detected. Plug one in and press any button to wake it
              up (browsers only expose a pad after its first input).
            </p>
          ) : (
            <div className="space-y-6">
              {gamepads.map((pad) => (
                <GamepadView key={pad.index} pad={pad} />
              ))}
            </div>
          )}
        </section>

        {/* Pro Controllers (WebHID) */}
        <section className="space-y-4 rounded-lg border-2 border-primary/30 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold font-title text-lg">
              Pro Controllers (WebHID)
            </h2>
            <span className="font-mono text-primary/60 text-sm">
              {proSnapshots.length} connected
            </span>
          </div>

          {!hidSupported ? (
            <p className="font-mono text-primary/40 text-sm">
              WebHID is unsupported in this browser. Use Chrome or Edge.
            </p>
          ) : proSnapshots.length === 0 ? (
            <p className="font-mono text-primary/40 text-sm">
              No Pro Controllers connected. Click{' '}
              <span className="text-primary">Connect controller</span> (bottom
              left), pick a controller, then press buttons to see which arcade
              control each one triggers.
            </p>
          ) : (
            <div className="space-y-6">
              {proSnapshots.map((snapshot) => (
                <ProControllerView key={snapshot.player} snapshot={snapshot} />
              ))}
            </div>
          )}
        </section>
      </div>

      {hidSupported && <ConnectControllerButton onConnect={hidConnect} />}
    </div>
  );
}

const PRO_BUTTON_ORDER: Array<keyof ProControllerButtons> = [
  'A',
  'B',
  'X',
  'Y',
  'L',
  'R',
  'ZL',
  'ZR',
  'up',
  'down',
  'left',
  'right',
  'minus',
  'plus',
  'home',
  'capture',
  'lStick',
  'rStick',
];

function ProControllerView({ snapshot }: { snapshot: ProControllerState }) {
  const player = snapshot.player === 2 ? 2 : 1;
  const arcadeControls = proControllerToArcadeControls(snapshot, player);
  const arcadeEntries = Object.entries(arcadeControls) as Array<
    [ArcadeControlCode, boolean]
  >;
  const batteryPct = Math.round((snapshot.battery / 8) * 100);

  return (
    <div className="space-y-3 rounded-md border border-primary/20 p-4">
      <div className="flex items-center justify-between font-mono text-sm">
        <span className="text-primary">Player {snapshot.player}</span>
        <span className="text-primary/50">battery {batteryPct}%</span>
      </div>

      <div className="space-y-1.5">
        <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
          Raw buttons
        </span>
        <div className="flex flex-wrap gap-1.5">
          {PRO_BUTTON_ORDER.map((name) => {
            const pressed = snapshot.buttons[name];
            return (
              <span
                key={name}
                className={`rounded border px-2 py-1 font-mono text-xs transition-colors ${
                  pressed
                    ? 'border-primary bg-primary/25 text-primary'
                    : 'border-primary/15 text-primary/40'
                }`}
              >
                {name}
              </span>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
          Mapped arcade controls
        </span>
        <div className="flex flex-wrap gap-1.5">
          {arcadeEntries.map(([code, pressed]) => (
            <span
              key={code}
              className={`rounded border px-2 py-1 font-mono text-xs transition-colors ${
                pressed
                  ? 'border-primary bg-primary/25 font-bold text-primary'
                  : 'border-primary/15 text-primary/40'
              }`}
            >
              {code}
            </span>
          ))}
          <span
            className={`rounded border px-2 py-1 font-mono text-xs transition-colors ${
              snapshot.buttons.minus
                ? 'border-primary bg-primary/25 font-bold text-primary'
                : 'border-primary/15 text-primary/40'
            }`}
          >
            BACK/COIN
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {(['leftStick', 'rightStick'] as const).map((stick) => (
          <div key={stick} className="space-y-1 font-mono text-xs">
            <div className="flex justify-between text-primary/50">
              <span>
                {stick === 'leftStick' ? 'left stick' : 'right stick'}
              </span>
              <span className="text-primary">
                {snapshot[stick].x.toFixed(2)}, {snapshot[stick].y.toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  span,
}: {
  label: string;
  value: string;
  span?: boolean;
}) {
  return (
    <div className={`bg-black p-3 ${span ? 'col-span-3' : ''}`}>
      <div className="text-primary/40 text-xs uppercase tracking-wider">
        {label}
      </div>
      <div className="mt-0.5 break-all text-primary">{value}</div>
    </div>
  );
}

function GamepadView({ pad }: { pad: GamepadSnapshot }) {
  return (
    <div className="space-y-3 rounded-md border border-primary/20 p-4">
      <div className="font-mono text-sm">
        <span className="text-primary/50">#{pad.index}</span>{' '}
        <span className="text-primary">{pad.id}</span>
      </div>

      <div className="space-y-1.5">
        <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
          Buttons
        </span>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(56px,1fr))] gap-1.5">
          {pad.buttons.map((button, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: button order is the identity
              key={i}
              className={`flex flex-col items-center rounded border px-1 py-1.5 font-mono transition-colors ${
                button.pressed
                  ? 'border-primary bg-primary/25 text-primary'
                  : 'border-primary/15 text-primary/40'
              }`}
            >
              <span className="font-bold text-sm">{i}</span>
              <span className="text-[10px]">{button.value.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="font-mono text-primary/50 text-xs uppercase tracking-widest">
          Axes
        </span>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {pad.axes.map((axis, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: axis order is the identity
              key={i}
              className="space-y-1 font-mono text-xs"
            >
              <div className="flex justify-between text-primary/50">
                <span>axis {i}</span>
                <span className="text-primary">{axis.toFixed(2)}</span>
              </div>
              <div className="relative h-1.5 overflow-hidden rounded-full bg-primary/15">
                <div
                  className="absolute top-0 bottom-0 bg-primary"
                  style={{
                    left: `${50 + Math.min(0, axis) * 50}%`,
                    right: `${50 - Math.max(0, axis) * 50}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
